import React, { memo, useEffect, useRef, useState } from 'react';
import { Flex, Text, ToastQueue } from '@adobe/react-spectrum';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { selectIsAuthenticated } from 'app/pages/Layout/slice/selectors';
import { selectLayout } from 'app/pages/Layout/slice/selectors';
import { useLayoutSlice } from 'app/pages/Layout/slice';
import type { SavedView } from 'app/services/savedViewsApi';
import { DeleteConfirmationDialog } from 'app/components/shared/DeleteConfirmationDialog/DeleteConfirmationDialog';
import { openEditSaveViewModal } from '../saveViewModalBridge';
import { useSavedViewsSlice } from '../slice/index';
import {
  selectSavedViews,
  selectSavedViewsDeleting,
  selectSavedViewsError,
  selectSavedViewsLoading
} from '../slice/selectors';
import { MY_STUFF_VIEW_ID, MY_STUFF_VIEW_NAME } from '../constants';
import sidebarStyles from 'app/components/RoadmapCard/leftSidebar.module.scss';
import styles from './SavedViewsSection.module.scss';
import { SavedViewsHeader } from './SavedViewsHeader';
import { SavedViewListItem } from './SavedViewListItem';
import type { useSavedViewActions } from './hooks/useSavedViewActions';

type SavedViewActions = ReturnType<typeof useSavedViewActions>;

interface SavedViewsSectionProps {
  activeSavedViewId: SavedViewActions['activeSavedViewId'];
  handleCopyLink: SavedViewActions['handleCopyLink'];
  handleOpenSavedView: SavedViewActions['handleOpenSavedView'];
  handleOpenMyStuff: SavedViewActions['handleOpenMyStuff'];
}

// Renders the Save Views list; the active-view state machine itself
// (opening grace period, filter baseline, divergence/deleted effects) lives
// in a single useSavedViewActions() instance owned by the parent LeftSidebar,
// so there's exactly one source of truth for it across the nav tree.
export const SavedViewsSection = memo(
  ({
    activeSavedViewId,
    handleCopyLink,
    handleOpenSavedView,
    handleOpenMyStuff
  }: SavedViewsSectionProps) => {
    const { actions: layoutActions } = useLayoutSlice();
    const { actions } = useSavedViewsSlice();
    const dispatch = useDispatch();
    const isAuthenticated = useSelector(selectIsAuthenticated);
    const { isSidebarOpen, isSavedViewsExpanded } = useSelector(selectLayout);
    const savedViews = useSelector(selectSavedViews);
    const loading = useSelector(selectSavedViewsLoading);
    const deleting = useSelector(selectSavedViewsDeleting);
    const sliceError = useSelector(selectSavedViewsError);

    const [openTooltip, setOpenTooltip] = useState<string | null>(null);
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const [viewToDelete, setViewToDelete] = useState<SavedView | null>(null);
    const tooltipTimerRef = useRef<NodeJS.Timeout | null>(null);
    const pendingDeleteRef = useRef(false);
    const prevDeletingRef = useRef(false);
    // Tracks the previous expanded state so the auto-select effect below fires
    // only on the collapsed->expanded edge, not on every render while expanded.
    const wasSavedViewsExpandedRef = useRef(isSavedViewsExpanded);

    useEffect(() => {
      if (isAuthenticated) {
        dispatch(actions.loadSavedViews());
      }
    }, [actions, dispatch, isAuthenticated]);

    // When Save Views transitions from collapsed to expanded with nothing
    // currently active (e.g. plain Roadmap, or just came from Must Wins -
    // exitActiveSavedView() already cleared activeSavedViewId for that case),
    // My Stuff becomes the active view. Guarded on activeSavedViewId === null
    // so that merely collapsing and re-expanding the list via the chevron
    // doesn't clobber a real Saved View (or My Stuff) that's already active.
    useEffect(() => {
      const wasExpanded = wasSavedViewsExpandedRef.current;
      wasSavedViewsExpandedRef.current = isSavedViewsExpanded;

      if (
        !wasExpanded &&
        isSavedViewsExpanded &&
        isAuthenticated &&
        activeSavedViewId === null
      ) {
        handleOpenMyStuff();
      }
    }, [
      isSavedViewsExpanded,
      isAuthenticated,
      activeSavedViewId,
      handleOpenMyStuff
    ]);

    useEffect(() => {
      return () => {
        if (tooltipTimerRef.current) {
          clearTimeout(tooltipTimerRef.current);
        }
      };
    }, []);

    useEffect(() => {
      const wasDeleting = prevDeletingRef.current;
      prevDeletingRef.current = deleting;

      if (!wasDeleting || deleting || !pendingDeleteRef.current) {
        return;
      }

      pendingDeleteRef.current = false;

      if (sliceError) {
        ToastQueue.negative(sliceError, { timeout: 5000 });
        return;
      }

      ToastQueue.positive('View deleted successfully.', { timeout: 5000 });
    }, [deleting, sliceError]);

    const handleHeaderTooltipMouseEnter = () => {
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current);
      }
      tooltipTimerRef.current = setTimeout(() => {
        setOpenTooltip('savedviews');
      }, 1000);
    };

    const handleHeaderTooltipMouseLeave = () => {
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current);
      }
      setOpenTooltip(null);
    };

    const toggleSidebar = () => {
      dispatch(layoutActions.toggleSidebar());
    };

    const setSavedViewsExpanded = (expanded: boolean) => {
      dispatch(layoutActions.setSavedViewsExpanded(expanded));
    };

    const handleLabelPress = () => {
      // The label only ever expands - collapsing is the chevron's job alone.
      if (!isSidebarOpen) {
        // Expand the nav rail and the Saved Views list together, so the
        // collapsed->expanded edge below fires and My Stuff gets selected -
        // mirrors handleChevronPress.
        toggleSidebar();
      }
      setSavedViewsExpanded(true);
    };

    const handleChevronPress = () => {
      if (!isSidebarOpen) {
        toggleSidebar();
      }
      setSavedViewsExpanded(!isSavedViewsExpanded);
    };

    const handleEdit = (savedView: SavedView) => {
      openEditSaveViewModal(savedView);
    };

    const handleConfirmDelete = () => {
      if (!viewToDelete) {
        return;
      }

      pendingDeleteRef.current = true;
      dispatch(actions.deleteView({ id: viewToDelete.id }));
      setViewToDelete(null);
    };

    const handleCancelDelete = () => {
      setViewToDelete(null);
    };

    if (!isAuthenticated) {
      return null;
    }

    // Built-in "My Stuff" row — always rendered first, independent of the
    // saved-views API call (loading/error/empty states below only apply to the
    // user-created list that follows it).
    const myStuffListItem = (
      <SavedViewListItem
        key={MY_STUFF_VIEW_ID}
        savedView={{
          id: MY_STUFF_VIEW_ID,
          name: MY_STUFF_VIEW_NAME,
          customViewLink: ''
        }}
        isBuiltIn
        isMenuOpen={false}
        isActive={activeSavedViewId === MY_STUFF_VIEW_ID}
        onMenuOpenChange={() => {}}
        onOpen={handleOpenMyStuff}
        onCopyLink={() => {}}
        onEdit={() => {}}
        onDelete={() => {}}
      />
    );

    const renderRestOfList = () => {
      if (loading && savedViews.length === 0) {
        return (
          <div className={sidebarStyles['must-wins-audience-item']}>
            <Text UNSAFE_className={styles.savedViewLoading}>Loading...</Text>
          </div>
        );
      }

      if (!loading && savedViews.length === 0 && sliceError) {
        return (
          <div
            className={sidebarStyles['must-wins-audience-item']}
            data-testid="saved-views-error-label"
          >
            <Text UNSAFE_className={styles.savedViewsEmptyLabel}>
              {sliceError}
            </Text>
          </div>
        );
      }

      if (savedViews.length === 0) {
        return (
          <div
            className={sidebarStyles['must-wins-audience-item']}
            data-testid="saved-views-empty-label"
          >
            <Text UNSAFE_className={styles.savedViewsEmptyLabel}>No views</Text>
          </div>
        );
      }

      return savedViews.map(savedView => (
        <SavedViewListItem
          key={savedView.id}
          savedView={savedView}
          isMenuOpen={openMenuId === savedView.id}
          isActive={activeSavedViewId === savedView.id}
          onMenuOpenChange={isOpen =>
            setOpenMenuId(isOpen ? savedView.id : null)
          }
          onOpen={handleOpenSavedView}
          onCopyLink={handleCopyLink}
          onEdit={handleEdit}
          onDelete={setViewToDelete}
        />
      ));
    };

    const renderListContent = () => (
      <>
        {myStuffListItem}
        {renderRestOfList()}
      </>
    );

    return (
      <>
        <Flex
          direction="row"
          UNSAFE_className={classNames(sidebarStyles['left-sidebar-nav-item'], {
            [sidebarStyles['left-sidebar-row-collapsed']]: !isSidebarOpen
          })}
        >
          <Flex
            direction="column"
            alignItems="start"
            UNSAFE_className={sidebarStyles['must-wins-wrapper']}
            data-testid="saved-views-section"
          >
            <SavedViewsHeader
              isSidebarOpen={isSidebarOpen}
              isExpanded={isSavedViewsExpanded}
              isActive={activeSavedViewId !== null}
              openTooltip={openTooltip}
              onTooltipOpenChange={isOpen => {
                if (!isOpen) {
                  setOpenTooltip(null);
                }
              }}
              onTooltipMouseEnter={handleHeaderTooltipMouseEnter}
              onTooltipMouseLeave={handleHeaderTooltipMouseLeave}
              onLabelPress={handleLabelPress}
              onChevronPress={handleChevronPress}
            />

            {isSavedViewsExpanded && isSidebarOpen && (
              <Flex
                direction="column"
                alignItems="start"
                UNSAFE_className={sidebarStyles['must-wins-audience-list']}
              >
                {renderListContent()}
              </Flex>
            )}
          </Flex>
        </Flex>

        <DeleteConfirmationDialog
          isOpen={viewToDelete !== null}
          title="Delete View?"
          message="Are you sure you want to delete this view? This action cannot be undone."
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      </>
    );
  }
);

SavedViewsSection.displayName = 'SavedViewsSection';
